import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, SubmitInput, Error, Success } from './styles';
import { useAdminFunction } from '../../hooks/useAdminFunction';
import { AWSStatusIndicator } from '../../components';

type RestoreUserStreakResponse = {
  success: boolean;
  message: string;
  restoredStreakCount: number;
  highestStreakEarned: number;
};

const RestoreUserStreaks: React.FC = () => {
  const navigate = useNavigate();
  const { callFunction, isAuthenticated } = useAdminFunction();
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleYesClick = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const target = e.target as typeof e.target & {
      userId: { value: string };
      streakCount: { value: string };
    };

    const userId = target.userId.value;
    const streakCount = target.streakCount.value;

    if (!userId || !streakCount || Number(streakCount) < 1) {
      alert('Invalid input: User ID is required and streak count must be a positive number');
      return;
    }

    setIsLoading(true);

    try {
      const result = (await callFunction('websiteInternalFunctions/restoreUserStreak', {
        userId,
        streakCount: Number(streakCount)
      })) as RestoreUserStreakResponse;

      setSuccess(
        `Successfully restored ${result.restoredStreakCount} streaks for user ${userId}. ` +
          `New highest streak: ${result.highestStreakEarned}`
      );
    } catch (error: any) {
      console.error('Failed to restore user streaks:', error);
      setError(error?.message || error?.error || 'An error occurred while restoring streaks');
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleNoClick = () => {};

  const handleAlert = (e: React.SyntheticEvent) => {
    const target = e.target as typeof e.target & {
      userId: { value: string };
      streakCount: { value: string };
    };

    const userId = target.userId.value;
    const streakCount = target.streakCount.value;

    const confirmed = window.confirm(
      `Are you sure you want to restore ${streakCount} streaks for user ${userId}? This will overwrite their current streaks.`
    );

    if (confirmed) {
      handleYesClick(e);
    } else {
      handleNoClick();
    }
  };

  return (
    <>
      <AWSStatusIndicator />
      <Form onSubmit={handleAlert}>
      <Input placeholder="User ID" name="userId" required />
      <Input placeholder="Streak Count" type="number" min="1" name="streakCount" required />
      <SubmitInput type="submit" value={isLoading ? "Restoring..." : "Restore Streaks"} disabled={isLoading || !isAuthenticated} />
        {error.length > 0 && <Error>{error}</Error>}
        {success.length > 0 && <Success>{success}</Success>}
      </Form>
    </>
  );
};

export { RestoreUserStreaks };
